import React from 'react';
import { X, ArrowRight, ArrowLeft, HelpCircle } from 'lucide-react';
import type { GuideStep, TooltipPositionState } from './types';
import { getTooltipArrowClass } from './helpers';

export interface GuideTooltipProps {
  tooltipRef: React.RefObject<HTMLDivElement | null>;
  currentGuideStep: GuideStep;
  tooltipPosition: TooltipPositionState;
  isPositioning: boolean;
  allowSkip: boolean;
  showProgress: boolean;
  steps: GuideStep[];
  currentStep: number;
  totalSteps: number;
  isLastStep: boolean;
  onSkip: () => void;
  onNext: () => void;
  onPrevious: () => void;
  onActionButton: () => void;
}

export const GuideTooltip: React.FC<GuideTooltipProps> = ({
  tooltipRef,
  currentGuideStep,
  tooltipPosition,
  isPositioning,
  allowSkip,
  showProgress,
  steps,
  currentStep,
  totalSteps,
  isLastStep,
  onSkip,
  onNext,
  onPrevious,
  onActionButton,
}) => {
  return (
    <div
      ref={tooltipRef as React.RefObject<HTMLDivElement>}
      className={`fixed guide-tooltip ${getTooltipArrowClass(tooltipPosition.position)} rounded-lg p-6 tooltip-slide-in focus:outline-hidden shadow-2xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700`}
      style={{
        top: tooltipPosition.top,
        left: tooltipPosition.left,
        zIndex: 10000,
        transform: isPositioning ? 'scale(0.95) opacity(0.5)' : 'scale(1) opacity(1)',
        transition: 'transform 0.2s ease-out, opacity 0.2s ease-out',
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="guide-title"
      aria-describedby="guide-content"
      tabIndex={-1}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2 flex-1">
          <HelpCircle className="w-5 h-5 text-teal-500 shrink-0" aria-hidden="true" />
          <div className="flex-1">
            <h3 id="guide-title" className="font-semibold text-gray-900 dark:text-white text-sm">
              {currentGuideStep.title}
            </h3>
            {/* {currentGuideStep.estimatedDuration && (
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                ~{currentGuideStep.estimatedDuration}s to read
              </div>
            )} */}
          </div>
        </div>
        {allowSkip && (
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onSkip();
            }}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1 focus:outline-hidden focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 rounded shrink-0"
            aria-label="Close guide and skip tutorial"
          >
            <X className="w-4 h-4" aria-hidden="true" />
          </button>
        )}
      </div>

      {/* Content */}
      <div className="mb-4">
        <p id="guide-content" className="text-gray-600 dark:text-gray-300 leading-relaxed text-sm">
          {currentGuideStep.content}
        </p>

        {/* Action Button */}
        {currentGuideStep.actionButton && (
          <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onActionButton();
              }}
              className={`w-full flex items-center justify-center gap-2 px-3 py-2 text-sm rounded-md transition-colors ${
                currentGuideStep.actionButton.variant === 'primary'
                  ? 'bg-teal-500 hover:bg-teal-600 text-white'
                  : currentGuideStep.actionButton.variant === 'secondary'
                    ? 'bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300'
                    : 'hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400'
              }`}
            >
              {currentGuideStep.actionButton.icon}
              {currentGuideStep.actionButton.text}
            </button>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between">
        {showProgress && (
          <div className="flex items-center gap-2">
            <span
              className="text-xs text-gray-500 dark:text-gray-400"
              aria-label={`Step ${currentStep + 1} of ${totalSteps}`}
            >
              {currentStep + 1} of {totalSteps}
            </span>
            <div
              className="flex gap-1"
              role="progressbar"
              aria-valuenow={currentStep + 1}
              aria-valuemin={1}
              aria-valuemax={totalSteps}
              aria-label={`Progress: ${Math.round(((currentStep + 1) / totalSteps) * 100)}% complete`}
            >
              {steps.map((step, index) => (
                <div
                  key={step.id}
                  className={`w-2 h-2 rounded-full transition-colors duration-200 ${
                    index === currentStep
                      ? 'bg-teal-500 scale-110'
                      : index < currentStep
                        ? 'bg-teal-300'
                        : 'bg-gray-300 dark:bg-gray-600'
                  } ${step.optional ? 'opacity-60' : ''}`}
                  aria-hidden="true"
                  title={step.optional ? 'Optional step' : undefined}
                />
              ))}
            </div>
            {/* Progress percentage */}
            <span className="text-xs text-gray-400 dark:text-gray-500 ml-1">
              {Math.round(((currentStep + 1) / totalSteps) * 100)}%
            </span>
          </div>
        )}

        <div className="flex items-center gap-2">
          {currentStep > 0 && (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onPrevious();
              }}
              className="btn-ghost text-sm px-3 py-1.5 flex items-center gap-1 focus:outline-hidden focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 rounded-md"
              aria-label="Go to previous step"
            >
              <ArrowLeft className="w-3 h-3" aria-hidden="true" />
              Back
            </button>
          )}
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onNext();
            }}
            className="btn-primary text-sm px-3 py-1.5 flex items-center gap-1 focus:outline-hidden focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 rounded-md"
            aria-label={isLastStep ? 'Finish tutorial' : 'Go to next step'}
          >
            {isLastStep ? 'Finish' : 'Next'}
            {!isLastStep && <ArrowRight className="w-3 h-3" aria-hidden="true" />}
          </button>
        </div>
      </div>
    </div>
  );
};
